import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import { X, Cookie } from 'lucide-react';
import { cn } from '../../lib/utils';

export function CookieConsent() {
  const [show, setShow] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem('cookie-consent');
    const consentTimestamp = localStorage.getItem('cookie-consent-timestamp');
    
    // Show banner if:
    // 1. No consent exists, OR
    // 2. Consent is older than 6 months (re-consent required)
    if (!consent) {
      // Delay showing banner slightly for better UX
      setTimeout(() => setShow(true), 1000);
    } else if (consentTimestamp) {
      const sixMonthsAgo = Date.now() - (6 * 30 * 24 * 60 * 60 * 1000);
      if (parseInt(consentTimestamp) < sixMonthsAgo) {
        setTimeout(() => setShow(true), 1000);
      }
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-consent-timestamp', Date.now().toString());
    handleClose();
    
    // Initialize analytics/tracking here if needed
    // Example: window.gtag && window.gtag('consent', 'update', { analytics_storage: 'granted' });
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    localStorage.setItem('cookie-consent-timestamp', Date.now().toString());
    handleClose();
    
    // Disable analytics/tracking here
    // Example: window.gtag && window.gtag('consent', 'update', { analytics_storage: 'denied' });
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShow(false);
      setIsClosing(false);
    }, 300);
  };

  if (!show) return null;

  return (
    <div
      className={cn(
        "fixed bottom-4 left-4 right-4 md:left-6 md:right-6 z-50 transition-all duration-300 ease-out",
        isClosing ? "translate-y-20 opacity-0" : "translate-y-0 opacity-100"
      )}
    >
      <div className="max-w-5xl mx-auto">
        <div className="bg-background/95 backdrop-blur-lg border border-border rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center gap-3 md:gap-4 p-3 md:p-4">
            {/* Icon */}
            <div className="flex-shrink-0 h-10 w-10 md:h-12 md:w-12 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
              <Cookie className="h-5 w-5 md:h-6 md:w-6 text-primary" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground leading-relaxed">
                We use cookies to enhance your experience.{' '}
                <Link 
                  to="/cookies" 
                  className="text-primary hover:underline font-medium whitespace-nowrap"
                >
                  Learn more
                </Link>
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDecline}
                className="hidden sm:inline-flex"
              >
                Decline
              </Button>
              <Button
                size="sm"
                onClick={handleAccept}
                className="bg-primary hover:bg-primary/90"
              >
                Accept
              </Button>
              <button
                onClick={handleClose}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
